/* eslint-disable react/react-in-jsx-scope */
import { useMsal } from '@azure/msal-react';
import { useContext, useState } from 'react';
import TenantContext from '../../lib/context/TenantContext';
import {
  ErrorCode,
  FileRejection,
  FileWithPath,
  useDropzone,
} from 'react-dropzone';
import UploadIcon from '../../assets/upload.svg';
import { uploadFilesInParallel } from '../../lib/services/blob-service';
import { sendMessageAsync } from '../../lib/services/servicebus-service';
import { SnackbarProps } from '../../pages/helper';
import useBatchProcessStore from '../../store/batchProcessStore';
import { acceptedFileTypes } from './helper';
import { format } from 'date-fns';

export interface DropzoneProps {
  setSnackbar: (props: SnackbarProps) => void;
}

const Dropzone = (props: DropzoneProps) => {
  const { setSnackbar } = props;
  const { accounts } = useMsal();
  const account = accounts[0];
  const { tenant } = useContext(TenantContext);
  const {
    setBatchId,
    setIsProcessing,
    acceptedFiles,
    setAcceptedFiles,
    setIsFileUploaded,
    resetState,
    isDownloadEnabled,
    setRejectedFiles,
  } = useBatchProcessStore();
  const todayStr = new Date().toISOString().slice(0, 10);
  const [voucherDate, setVoucherDate] = useState<string | null>(todayStr);

  const onDrop = (accepted: FileWithPath[], rejection: FileRejection[]) => {
    if (accepted.length > 0) {
      setBatchId(undefined);
    }
    setAcceptedFiles(accepted.map(a => Object.assign(a, { status: 0 })));
    const rejectedFiles = rejection.map(r => ({
      file: r.file,
      errors: [...r.errors],
    }));
    setRejectedFiles(rejectedFiles);
    resetState();
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    maxFiles: 10,
    maxSize: 15 * 1024 ** 2,
    onDrop(accepted, rejected) {
      onDrop(accepted, rejected);
    },
    onDropRejected(fileRejections) {
      if (
        fileRejections.every(e =>
          e.errors.some(er => er.code === ErrorCode.FileTooLarge),
        )
      ) {
        setSnackbar({
          show: true,
          errorMessage: 'Maximum allowed file size is 15MB.',
        });
      }
      if (
        fileRejections.every(e =>
          e.errors.some(er => er.code === ErrorCode.TooManyFiles),
        )
      ) {
        setSnackbar({
          show: true,
          errorMessage: 'Maximum number of files allowed per batch is 10.',
        });
      }
    },
  });

  const handleUploadFiles = async () => {
    if (!tenant.id) {
      setSnackbar({
        show: true,
        errorMessage: 'TenantId is not setup. Please contact admin',
      });
    } else if (acceptedFiles.length > 0) {
      setIsProcessing(true);
      const resp = await uploadFilesInParallel(
        acceptedFiles,
        `${account?.name}`,
      );
      setIsFileUploaded(true);
      // Convert voucher date to dd-MMM-yyyy format
      let finalVoucherDate: string | null = null;
      if (voucherDate && voucherDate.trim() !== '') {
        const dateObj = new Date(voucherDate);
        finalVoucherDate = format(dateObj, 'dd-MMM-yyyy');
      }
      await sendMessageAsync({
        body: {
          voucherDate: finalVoucherDate,
          batchId: resp.batchId,
          filePath: resp.filePath,
          files: resp.fileInfos,
          clientId: tenant.id,
          author: account?.name,
          userId: account?.idTokenClaims?.preferred_username,
        },
      });
      setBatchId(resp.batchId);
      sessionStorage.setItem('filePath', resp.filePath);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-6 overflow-x-hidden">
      <div className="flex flex-col items-center justify-center text-center mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 text-center">
          <span className="flex items-center justify-center gap-2">
            Invoice Upload
          </span>
        </h2>
        <p className="text-gray-500 mt-2 text-xs sm:text-sm max-w-md">
          Upload your invoice files and set a voucher date (optional). You can
          upload up to 10 files at a time.
        </p>
      </div>
      <div className="mb-4">
        <div
          {...getRootProps({
            className:
              'flex flex-col items-center justify-center bg-blue-50 border-2 border-dashed border-blue-400 rounded-xl p-2 sm:p-4 cursor-pointer hover:bg-blue-100 transition overflow-x-hidden w-full min-h-[120px]',
          })}>
          <input {...getInputProps()} title="files" />
          <div className="flex flex-col items-center w-full">
            <img
              src={UploadIcon}
              alt="upload"
              className="w-7 h-7 sm:w-8 sm:h-8 mb-1 opacity-70"
            />
            <span className="text-sm sm:text-base font-semibold text-blue-700 text-center">
              Drag & drop or click to select files
            </span>
            <span className="text-xs text-gray-500 mt-1 text-center">
              (PDF, JPEG, PNG | Max 10 files | Max 15MB each)
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mt-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 w-full sm:w-auto">
          <label
            htmlFor="voucher-date"
            className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap mb-1 sm:mb-0">
            Voucher Date
          </label>
          <input
            id="voucher-date"
            type="date"
            className="rounded border border-gray-300 px-2 py-1.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors bg-white w-full sm:w-auto"
            value={voucherDate ?? ''}
            onChange={e => setVoucherDate(e.target.value || null)}
            max={new Date().toISOString().slice(0, 10)}
            placeholder="Select date"
          />
          <span className="text-xs text-gray-500 sm:ml-1">(Optional)</span>
        </div>
        <button
          className={`flex items-center justify-center gap-2 w-full sm:w-48 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 p-2 sm:p-3 shadow-lg hover:from-blue-600 hover:to-blue-800 text-white font-semibold text-base sm:text-lg transition disabled:opacity-50 disabled:pointer-events-none`}
          onClick={handleUploadFiles}
          disabled={
            acceptedFiles.length === 0 ||
            isDownloadEnabled ||
            !account?.idTokenClaims?.preferred_username
          }
          type="button">
          <img
            src={UploadIcon}
            alt="upload"
            className="w-5 h-5 sm:w-6 sm:h-6"
          />
          Upload
        </button>
      </div>
    </div>
  );
};

export default Dropzone;
